/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import {
  Column,
  Row,
  Table as TanStackTable,
  flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { CSSProperties, FC, Fragment, ReactNode, useState } from 'react';

import { ClientPod } from '../generated/graphql';
import { useAppSelector } from '../store/store';
import { getControlFieldLabelForPod } from '../utilities/podBasedFiltering';

interface Props<T extends object> {
  tableInstance: TanStackTable<T>;
  loading?: boolean;
  isRequest?: boolean;
  renderMobileCard?: (row: Row<T>) => ReactNode;
  tableLabel?: string;
}

const getCommonPinningStyles = (column: Column<any>): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn =
    isPinned === 'right' && column.getIsFirstColumn('right');

  return {
    boxShadow: isLastLeftPinnedColumn
      ? '-2px 0 2px -2px rgba(128, 128, 128, 1) inset'
      : isFirstRightPinnedColumn
        ? '2px 0 2px -2px rgba(128, 128, 128, 1) inset'
        : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    minWidth: isPinned ? column.getSize() : undefined,
    zIndex: isPinned ? 1 : 0,
  };
};

const Loading: FC<{ loading: boolean }> = ({ loading }) => (
  <div
    role="status"
    className={classNames('absolute left-0 right-0 top-20 z-10 h-10', {
      invisible: !loading,
    })}
  >
    <svg
      aria-hidden="true"
      className="m-auto size-8 animate-spin fill-accentGreen text-gray-200 dark:text-gray-600"
      viewBox="0 0 100 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        fill="currentColor"
      />
      <path
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill="currentFill"
      />
    </svg>
    <span className="sr-only">Loading...</span>
  </div>
);

const NoResults: FC<{ noResults: boolean }> = ({ noResults }) => (
  <div
    role="status"
    className={classNames('absolute left-0 right-0 top-20 z-10 h-10', {
      invisible: !noResults,
    })}
  >
    <div className="text-center">
      <div className="text-gray-500">
        <div className="mb-3 text-xl">🔍 No results found</div>
        <div>Try adjusting your filters or search criteria</div>
      </div>
    </div>
  </div>
);

interface MobileCardProps<T extends object> {
  row: any;
  tableInstance: TanStackTable<T>;
  clientPod?: ClientPod | null;
}

const RequestMobileCard = <T extends object>({
  row,
  clientPod,
}: MobileCardProps<T>) => {
  const cells = row.getVisibleCells();
  return (
    <div
      className={classNames(
        'mb-4 rounded-lg border border-gray-200 bg-secondaryBlue',
      )}
    >
      <div className="flex items-center p-4">
        <div className="flex w-full flex-col gap-4">
          {/* Main Info */}
          <div className="flex items-center justify-between">
            <div className="font-medium text-white">
              {flexRender(
                cells[0].column.columnDef.cell,
                cells[0].getContext(),
              )}
            </div>
            <div className="font-medium text-white">
              Added:{' '}
              {new Date(cells[6].getValue() as string).toLocaleDateString()}
            </div>
          </div>
          <div className="text-sm font-medium text-white">
            {flexRender(cells[3].column.columnDef.cell, cells[3].getContext())}
            {` (ID ${cells[1].getValue()})`}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 bg-white p-4">
        <p>
          {`${getControlFieldLabelForPod(clientPod)}: `}
          {flexRender(cells[2].column.columnDef.cell, cells[2].getContext())}
        </p>
        <p>
          {'Requested By: '}
          {flexRender(cells[5].column.columnDef.cell, cells[5].getContext())}
        </p>
        <p>
          {'Group: '}
          {flexRender(cells[4].column.columnDef.cell, cells[4].getContext())}
        </p>
      </div>
    </div>
  );
};
const MobileCard = <T extends object>({ row }: MobileCardProps<T>) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { pod: clientPod } = useAppSelector((state) => state.clientConfig);
  const cells = row.getVisibleCells();

  // Get the first few important cells for the main card display
  const mainCells = cells.slice(0, 3); // Checkbox, Name, ID
  const expandedCells = cells.slice(3); // Rest of the information

  return (
    <div
      className={classNames(
        'mb-4 rounded-lg border border-gray-200 bg-primaryBlue',
      )}
    >
      <div className="flex items-center justify-between p-4">
        <div className="flex flex-col items-center gap-4">
          {/* Main Info */}
          <div className="flex items-center gap-x-3">
            <div className="font-medium text-white">
              {flexRender(
                mainCells[0].column.columnDef.cell,
                mainCells[0].getContext(),
              )}
            </div>
            <div className="font-medium text-white">
              {flexRender(
                mainCells[1].column.columnDef.cell,
                mainCells[1].getContext(),
              )}
            </div>
          </div>
          <div className="text-sm font-light text-gray-300">
            {'DoD ID: '}
            {flexRender(
              mainCells[2].column.columnDef.cell,
              mainCells[2].getContext(),
            )}
          </div>
        </div>

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="rounded-full p-1"
        >
          <ChevronDownIcon
            color="white"
            className={classNames('size-6 transition-transform', {
              'rotate-180': isExpanded,
            })}
          />
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-4 border-t bg-white">
          <div
            className="flex flex-col gap-y-1 p-4"
            style={{
              backgroundColor:
                expandedCells[4].column.columnDef.cell === 'IN_HOUSE'
                  ? '#ECFDF5'
                  : '#EFF6FF',
            }}
          >
            <div
              className="text-sm font-medium"
              style={{
                color:
                  expandedCells[4].column.columnDef.cell === 'IN_HOUSE'
                    ? '#027A48'
                    : '#1E40AF',
              }}
            >
              {flexRender(
                expandedCells[4].column.columnDef.cell,
                expandedCells[4].getContext(),
              )}
            </div>
            <div className="font-medium">
              {flexRender(
                expandedCells[7].column.columnDef.cell,
                expandedCells[7].getContext(),
              )}
            </div>
            <div className="flex items-center gap-x-2 font-normal">
              {flexRender(
                expandedCells[8].column.columnDef.cell,
                expandedCells[8].getContext(),
              )}
              {', '}
              {flexRender(
                expandedCells[9].column.columnDef.cell,
                expandedCells[9].getContext(),
              )}
              {flexRender(
                expandedCells[10].column.columnDef.cell,
                expandedCells[10].getContext(),
              )}
            </div>
          </div>
          <div className="grid grid-cols-2">
            {/* Left Column */}
            <div className="border border-gray-200">
              <div className="flex items-center gap-x-2 border-b border-gray-200 p-2">
                <div className="text-sm font-medium text-gray-500">
                  Check-in:
                </div>
                <div className="text-sm">
                  {flexRender(
                    expandedCells.find(
                      (cell: any) =>
                        cell.column.columnDef.header === 'CHECK-IN',
                    )?.column.columnDef.cell,
                    expandedCells
                      .find(
                        (cell: any) =>
                          cell.column.columnDef.header === 'CHECK-IN',
                      )
                      ?.getContext(),
                  )}
                </div>
              </div>
              <div className="flex items-center gap-x-2 border-b border-gray-200 p-2">
                <div className="text-sm font-medium text-gray-500">
                  Room type:
                </div>
                <div className="text-sm">
                  {flexRender(
                    expandedCells.find(
                      (cell: any) =>
                        cell.column.columnDef.header === 'ROOM TYPE',
                    )?.column.columnDef.cell,
                    expandedCells
                      .find(
                        (cell: any) =>
                          cell.column.columnDef.header === 'ROOM TYPE',
                      )
                      ?.getContext(),
                  )}
                </div>
              </div>
              <div className="flex items-center gap-x-2 p-2">
                <div className="text-sm font-medium text-gray-500">
                  {getControlFieldLabelForPod(clientPod)}:
                </div>
                <div className="text-sm">
                  {flexRender(
                    expandedCells.find(
                      (cell: any) =>
                        cell.column.columnDef.header ===
                        getControlFieldLabelForPod(clientPod),
                    )?.column.columnDef.cell,
                    expandedCells
                      .find(
                        (cell: any) =>
                          cell.column.columnDef.header ===
                          getControlFieldLabelForPod(clientPod),
                      )
                      ?.getContext(),
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="border border-gray-200">
              <div className="flex items-center gap-x-2 border-b border-gray-200 p-2">
                <div className="text-sm font-medium text-gray-500">
                  Check-out:
                </div>
                <div className="text-sm">
                  {flexRender(
                    expandedCells.find(
                      (cell: any) =>
                        cell.column.columnDef.header === 'CHECK-OUT',
                    )?.column.columnDef.cell,
                    expandedCells
                      .find(
                        (cell: any) =>
                          cell.column.columnDef.header === 'CHECK-OUT',
                      )
                      ?.getContext(),
                  )}
                </div>
              </div>
              <div className="flex items-center gap-x-2 border-b border-gray-200 p-2">
                <div className="text-sm font-medium text-gray-500">Room #:</div>
                <div className="text-sm">
                  {flexRender(
                    expandedCells.find(
                      (cell: any) => cell.column.columnDef.header === 'ROOM #',
                    )?.column.columnDef.cell,
                    expandedCells
                      .find(
                        (cell: any) =>
                          cell.column.columnDef.header === 'ROOM #',
                      )
                      ?.getContext(),
                  )}
                </div>
              </div>
              <div className="flex items-center gap-x-2 p-2">
                <div className="text-sm font-medium text-gray-500">Rate:</div>
                <div className="text-sm">
                  {flexRender(
                    expandedCells.find(
                      (cell: any) => cell.column.columnDef.header === 'RATE',
                    )?.column.columnDef.cell,
                    expandedCells
                      .find(
                        (cell: any) => cell.column.columnDef.header === 'RATE',
                      )
                      ?.getContext(),
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Table = <T extends object>({
  tableInstance,
  loading = false,
  isRequest = false,
  renderMobileCard,
  tableLabel,
}: Props<T>) => {
  const hasPinnedColumns = tableInstance.getIsSomeColumnsPinned();
  const noResults = !loading && tableInstance.getRowCount() === 0;
  const { pod: clientPod } = useAppSelector((state) => state.clientConfig);

  return (
    <div className="relative min-h-40">
      <div
        className={classNames(
          'overflow-hidden border border-gray-200 duration-300',
          {
            'pointer-events-none bg-white opacity-60': loading || noResults,
          },
        )}
      >
        <Loading loading={loading} />
        <NoResults noResults={noResults} />

        {/* Mobile View */}
        <div className="sm:hidden">
          <div className="p-4">
            {tableInstance
              .getRowModel()
              .rows.map((row) =>
                renderMobileCard ? (
                  <Fragment key={row.id}>{renderMobileCard(row)}</Fragment>
                ) : isRequest ? (
                  <RequestMobileCard
                    key={row.id}
                    row={row}
                    tableInstance={tableInstance}
                    clientPod={clientPod}
                  />
                ) : (
                  <MobileCard
                    key={row.id}
                    row={row}
                    tableInstance={tableInstance}
                  />
                ),
              )}
          </div>
        </div>

        {/* Desktop View */}
        <div className="hidden overflow-x-scroll sm:block">
          <table
            aria-label={tableLabel}
            className={classNames('min-w-full divide-y divide-gray-200', {
              'table-fixed': hasPinnedColumns,
            })}
          >
            <thead>
              {tableInstance.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const { column } = header;

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className={classNames(
                          'text-nowrap bg-white pl-10 pr-2 py-3 text-left text-sm font-semibold text-gray-500',
                          {
                            'sm-max:!relative sm-max:!left-auto':
                              column.getIsPinned(),
                          },
                        )}
                        style={{ ...getCommonPinningStyles(column) }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            role="presentation"
                            className={classNames(
                              'group inline-flex select-none items-center',
                              {
                                'cursor-pointer': header.column.getCanSort(),
                              },
                            )}
                            onClick={header.column.getToggleSortingHandler()}
                            title={
                              header.column.getCanSort()
                                ? header.column.getNextSortingOrder() === 'asc'
                                  ? 'Sort ascending'
                                  : header.column.getNextSortingOrder() ===
                                      'desc'
                                    ? 'Sort descending'
                                    : 'Clear sort'
                                : undefined
                            }
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {{
                              asc: (
                                <ChevronUpIcon
                                  className={classNames(
                                    'size-5 ml-2 flex-none rounded',
                                    {
                                      invisible: !header.column.getCanSort(),
                                    },
                                    {
                                      'invisible text-gray-400 group-hover:visible':
                                        !header.column.getIsSorted(),
                                    },
                                    {
                                      'h-5 bg-gray-200 text-gray-900 group-hover:bg-gray-300':
                                        header.column.getIsSorted(),
                                    },
                                  )}
                                  aria-hidden="true"
                                />
                              ),
                              desc: (
                                <ChevronDownIcon
                                  className={classNames(
                                    'size-5 ml-2 flex-none rounded',
                                    {
                                      invisible: !header.column.getCanSort(),
                                    },
                                    {
                                      'invisible text-gray-400 group-hover:visible':
                                        !header.column.getIsSorted(),
                                    },
                                    {
                                      'h-5 bg-gray-200 text-gray-900 group-hover:bg-gray-300':
                                        header.column.getIsSorted(),
                                    },
                                  )}
                                  aria-hidden="true"
                                />
                              ),
                            }[header.column.getIsSorted() as string] ?? (
                              <ChevronDownIcon
                                className={classNames(
                                  'ml-2 h-5 w-5 flex-none rounded',
                                  {
                                    invisible: !header.column.getCanSort(),
                                  },
                                  {
                                    'invisible text-gray-400 group-hover:visible':
                                      !header.column.getIsSorted() &&
                                      header.column.getCanSort(),
                                  },
                                  {
                                    'h-5 bg-gray-200 text-gray-900 group-hover:bg-gray-300':
                                      header.column.getIsSorted() &&
                                      header.column.getCanSort(),
                                  },
                                )}
                                aria-hidden="true"
                              />
                            )}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableInstance.getRowModel().rows.map((row, i) => (
                <tr
                  key={row.id}
                  className={i % 2 === 0 ? 'bg-white' : 'bg-gray-100'}
                >
                  {row.getVisibleCells().map((cell) => {
                    const { column } = cell;
                    const { meta } = tableInstance.options;
                    let customCellClass: string | undefined;
                    if (
                      meta &&
                      'getCellClass' in meta &&
                      typeof meta.getCellClass === 'function'
                    ) {
                      customCellClass = meta.getCellClass(cell);
                    }

                    return (
                      <td
                        key={cell.id}
                        style={{ ...getCommonPinningStyles(column) }}
                        className={classNames(
                          'text-ellipsis whitespace-nowrap pl-10 pr-2 py-3 text-sm',
                          {
                            'sm-max:!relative sm-max:!left-auto':
                              column.getIsPinned(),
                          },
                          customCellClass,
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Table;
